import { useEffect, useState } from 'react';

import { searchPlace, type NatalPlace } from '@/api/client';

/**
 * Place autocomplete backed by Nominatim (via /v1/natal/search-place).
 *
 * Debounce: 350ms so we don't hammer Nominatim while the user is still
 * typing. The endpoint already rate-limits to 1 req/sec but the client
 * debouncing is what keeps individual users polite (and snappy — fewer
 * round trips means less perceived lag).
 *
 * onChange receives (display_name, lat, lon). When the user types
 * without picking a suggestion, lat/lon come back as null — the parent
 * should treat that as "place text changed but coordinates not yet
 * resolved" and require a re-pick before submitting.
 *
 * Lives in /components/ because both Profile (own birth place) and
 * Synastry (partner's birth place) need the same widget. Originally
 * inline in Profile.tsx; extracted here when synastry came online.
 */
export default function PlaceAutocomplete({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (place: string, lat: number | null, lon: number | null) => void;
}) {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState<NatalPlace[]>([]);
  const [open, setOpen] = useState(false);

  // Mirror the parent's `value` into our query state when it changes
  // externally (e.g. profile reset, form reset). Otherwise the input
  // freezes with stale text.
  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    if (q === value && !open) {
      return;
    }
    const handle = window.setTimeout(() => {
      searchPlace(q).then((r) => setSuggestions(r.places)).catch(() => {});
    }, 350);
    return () => window.clearTimeout(handle);
  }, [query, value, open]);

  const pick = (p: NatalPlace) => {
    setQuery(p.display_name);
    setSuggestions([]);
    setOpen(false);
    onChange(p.display_name, p.lat, p.lon);
  };

  return (
    <label className="relative block">
      <span className="mb-1 block text-[10px] uppercase tracking-widest text-gold-500/60">
        {label}
      </span>
      <input
        type="text"
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          // Typed text propagated up immediately; lat/lon only follow
          // when the user explicitly picks a suggestion below.
          onChange(e.target.value, null, null);
        }}
        onBlur={() => {
          // Slight delay so the click on a suggestion fires before
          // we close the dropdown.
          window.setTimeout(() => setOpen(false), 150);
        }}
        maxLength={200}
        className="w-full rounded-lg border border-gold-500/30
                   bg-night-900/60 px-3 py-2 font-serif text-base text-gold-100
                   outline-none transition focus:border-gold-400"
      />
      {open && suggestions.length > 0 && (
        <ul className="absolute left-0 right-0 z-30 mt-1 max-h-56 overflow-y-auto
                       rounded-lg border border-gold-500/30 bg-night-900
                       shadow-[0_10px_24px_rgba(0,0,0,0.4)]">
          {suggestions.map((p, i) => (
            <li key={`${p.lat}-${p.lon}-${i}`}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}    /* keep input focused so picking still fires */
                onClick={() => pick(p)}
                className="block w-full px-3 py-2 text-left text-sm text-gold-200
                           transition hover:bg-night-800 hover:text-gold-100"
              >
                {p.display_name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </label>
  );
}
